import type { SocketClient } from '@ping/ui';

import { isFeatureEnabled } from '@core/featureFlags';
import { log } from '@core/log/log';

import { getAccessToken } from './interceptor';
import { createSocket } from './socket';

interface SocketAdapterOptions {
  onReconnect?: () => Promise<void>;
}

export function createSocketAdapter(options: SocketAdapterOptions = {}): SocketClient {
  return {
    connect(url: string) {
      if (!isFeatureEnabled('FF_WEB_WS')) {
        log.info({ event: 'socketAdapter.connect', reason: 'FF_WEB_WS disabled, returning stub' });
        return createStubSocket();
      }

      log.info({ event: 'socketAdapter.connect', url });

      const socket = createSocket({
        url,
        getToken: getAccessToken,
        ...(options.onReconnect != null ? { onReconnect: options.onReconnect } : {}),
      });

      let messageHandler: ((data: string | ArrayBuffer) => void) | null = null;
      let closeHandler: ((code?: number, reason?: string) => void) | null = null;
      let reconnectHandler: (() => void) | null = null;

      const subscription = socket.subscribe(
        (data: unknown) => {
          if (messageHandler != null) {
            if (data instanceof ArrayBuffer) {
              messageHandler(data);
            } else if (typeof data === 'string') {
              messageHandler(data);
            } else {
              const serialized = JSON.stringify(data);
              messageHandler(serialized);
            }
          }
        },
        (state) => {
          log.debug({ event: 'socketAdapter.state', state });

          if (state === 'connected' && reconnectHandler != null) {
            reconnectHandler();
          } else if (state === 'disconnected' && closeHandler != null) {
            closeHandler();
          }
        },
      );

      return {
        send(data: string | ArrayBuffer): void {
          log.debug({
            event: 'socketAdapter.send',
            dataType: data instanceof ArrayBuffer ? 'ArrayBuffer' : 'string',
            byteLength: data instanceof ArrayBuffer ? data.byteLength : data.length,
          });
          // W1: actually put the frame on the wire (resume cursors + lifecycle
          // frames). The socket routes leader→ws / follower→leader internally.
          socket.send(data);
        },

        close(): void {
          log.info({ event: 'socketAdapter.close' });
          subscription.unsubscribe();
        },

        onMessage(handler: (data: string | ArrayBuffer) => void): void {
          messageHandler = handler;
        },

        onClose(handler: (code?: number, reason?: string) => void): void {
          closeHandler = handler;
        },

        onReconnect(handler: () => void): void {
          reconnectHandler = handler;
        },
      };
    },
  };
}

function createStubSocket() {
  return {
    send(): void {},
    close(): void {},
    onMessage(): void {},
    onClose(): void {},
    onReconnect(): void {},
  };
}
